import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Search,
  RefreshCw
} from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';

const GoogleAdsAccountList = () => {
  const [accounts, setAccounts] = useState([]);
  // Auth helper
  const getAuthHeaders = () => {
    const token = localStorage.getItem('gads_token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': 'Bearer ' + token } : {}),
    };
  };

  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: 'customer_id',
    direction: 'desc'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });
  const [syncing, setSyncing] = useState({});
  const navigate = useNavigate();

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/accounts?` +
        `page=${pagination.page}&` +
        `limit=${pagination.limit}&` +
        `search=${encodeURIComponent(searchTerm)}&` +
        `sortBy=${sortConfig.key}&` +
        `sortOrder=${sortConfig.direction}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch accounts');
      }
      const data = await response.json();
      setAccounts(data.accounts);
      setPagination(prev => ({
        ...prev,
        total: data.pagination.total,
        totalPages: data.pagination.totalPages
      }));
    } catch (err) {
      console.error('Error fetching accounts:', err);
      toast.error('Không thể tải danh sách tài khoản');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm, sortConfig]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleSyncCampaigns = async (customerId) => {
    try {
      setSyncing(prev => ({ ...prev, [customerId]: true }));
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/accounts/${customerId}/sync-campaigns`, { method: 'POST', headers: getAuthHeaders() });

      if (!response.ok) {
        throw new Error('Failed to sync campaigns');
      }

      toast.success('Đồng bộ chiến dịch thành công');
      // Refresh danh sách account
      fetchAccounts();
    } catch (err) {
      console.error('Error syncing campaigns:', err);
      toast.error('Đồng bộ chiến dịch thất bại');
    } finally {
      setSyncing(prev => ({ ...prev, [customerId]: false }));
    }
  };

  const handleDelete = async (customerId) => {
    try {
      if (!window.confirm('Bạn có chắc chắn muốn xóa tài khoản này?')) {
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/accounts/${customerId}`, { method: 'DELETE', headers: getAuthHeaders() });

      if (!response.ok) {
        throw new Error('Failed to delete account');
      }

      toast.success('Xóa tài khoản thành công');
      // Refresh danh sách account
      fetchAccounts();
    } catch (err) {
      console.error('Error deleting account:', err);
      toast.error('Xóa tài khoản thất bại');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Chưa đồng bộ';
    const date = new Date(dateString);
    return date.toLocaleString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-md font-bold">Danh sách tài khoản Google Ads</h2>
        <Button
                onClick={() => window.location.href = `${process.env.REACT_APP_API_URL}/api/oauth/auth`}
              className="inline-flex items-center px-2 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              Thêm tài khoản
            </Button>
      </div>
      {/* Search and Per Page Select - Always visible */}
      <div className="flex items-center justify-between mb-4 bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Tìm kiếm theo tên hoặc ID..."
              value={searchTerm}
              onChange={handleSearch}
              className="pl-8 w-[300px] bg-white"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Hiển thị:</span>
          <Select
            value={pagination.limit.toString()}
            onValueChange={(value) => {
              setPagination(prev => ({
                ...prev,
                limit: parseInt(value),
                page: 1
              }));
            }}
          >
            <SelectTrigger className="w-[100px] bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>

        </div>
      </div>

      <div className="rounded-md border overflow-x-auto bg-white">
        <div className="min-w-[1200px] relative">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead className="w-[250px] bg-white text-[12px]">
                  <Button
                    variant="ghost"
                    onClick={() => handleSort('name')}
                    className="flex items-center gap-1 text-[12px]"
                  >
                    Tên tài khoản
                    {sortConfig.key === 'name' && (
                      <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </Button>
                </TableHead>
                <TableHead className="w-[100px] bg-white text-center text-[12px]">
                  Trạng thái
                </TableHead>
                <TableHead className="w-[150px] bg-white text-center text-[12px]">
                  ID
                </TableHead>
                <TableHead className="w-[150px] bg-white text-right text-[12px]">
                  Tổng chiến dịch
                </TableHead>
                <TableHead className="w-[200px] bg-white text-right text-[12px]">
                  Chiến dịch hoạt động
                </TableHead>
                <TableHead className="w-[200px] bg-white text-right text-[12px]">
                  Lần đồng bộ cuối
                </TableHead>
                <TableHead className="w-[100px] text-right text-[12px] sticky right-0 bg-white z-10 shadow-[-4px_0_4px_-4px_rgba(0,0,0,0.1)]">Thao tác</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="bg-white">
              {loading ? (
                // Loading skeleton
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index} className="bg-white">
                    <TableCell className="bg-white"><Skeleton className="h-4 w-[250px]" /></TableCell>
                    <TableCell className="bg-white"><Skeleton className="h-4 w-[100px]" /></TableCell>
                    <TableCell className="bg-white"><Skeleton className="h-4 w-[100px]" /></TableCell>
                    <TableCell className="bg-white"><Skeleton className="h-4 w-[100px]" /></TableCell>
                    <TableCell className="bg-white"><Skeleton className="h-4 w-[100px]" /></TableCell>
                    <TableCell className="bg-white"><Skeleton className="h-4 w-[150px]" /></TableCell>
                    <TableCell className="text-right bg-white">
                      <div className="flex justify-end gap-1">
                        <Skeleton className="h-8 w-8" />
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : accounts.length === 0 ? (
                <TableRow className="bg-white">
                  <TableCell colSpan={7} className="text-center py-8 bg-white">
                    <div className="flex flex-col items-center justify-center text-gray-500">
                      <AlertCircle className="h-8 w-8 mb-2" />
                      <p>Không tìm thấy tài khoản nào</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                accounts.map((account) => (
                  <TableRow key={account.customer_id} className="hover:bg-gray-50 bg-white">
                    <TableCell className="w-[250px] bg-white text-[12px]">{account.name}</TableCell>
                    <TableCell className="w-[150px] flex items-center bg-white justify-center">
                      <Badge className={account.status === '2' ? 'bg-green-100 text-green-800 text-[12px]' : 'bg-red-100 text-red-800 text-[12px]'}>
                        {account.status === '2' ? 'Hoạt động' : 'Không hoạt động'}
                      </Badge>
                    </TableCell>
                    <TableCell className="w-[150px] bg-white text-center text-[12px]">{account.customer_id}</TableCell>
                    <TableCell className="w-[150px] bg-white text-[12px] text-right">{account.total_campaigns}</TableCell>
                    <TableCell className="w-[200px] bg-white text-[12px] text-right">{account.active_campaigns}</TableCell>
                    <TableCell className="w-[200px] bg-white text-[12px] text-right">{formatDate(account.last_sync)}</TableCell>
                    <TableCell className="w-[100px] text-right sticky right-0 bg-white z-10 shadow-[-4px_0_4px_-4px_rgba(0,0,0,0.1)]">
                      <div className="flex items-center justify-end gap-1">
                        <Button className="h-6 w-6"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleSyncCampaigns(account.customer_id)}
                          title="Đồng bộ chiến dịch"
                          disabled={syncing[account.customer_id]}
                        >
                          <RefreshCw className={`h-4 w-4 ${syncing[account.customer_id] ? 'animate-spin' : ''}`} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(account.customer_id)}
                          title="Xóa"
                          className="text-red-500 hover:text-red-700 h-6 w-6"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination - Always visible */}
      <div className="flex items-center justify-between mt-4 bg-white p-4 rounded-lg shadow-sm">
        <div className="text-sm text-gray-500">
          Hiển thị {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} trên {pagination.total} kết quả
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1 || loading}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="text-sm">
            Trang {pagination.page} / {pagination.totalPages}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page === pagination.totalPages || loading}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GoogleAdsAccountList; 